import { motion } from 'framer-motion';
import { SplitWords, FadeSlideIn } from './AnimatedText';
import { TEST_IDS } from '@/constants/testIds';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const FAQ_ITEMS = [
  {
    question: 'How is this different from therapy or business consulting?',
    answer: 'Coaching is action-oriented and forward-looking. Unlike therapy, we focus on professional growth and leadership challenges. Unlike consulting, I don\'t deliver recommendations — I help you find your own answers and build the capacity to lead through complexity.',
  },
  {
    question: 'How long does a typical engagement last?',
    answer: 'Most engagements run 3–6 months, though some leaders continue on a lighter cadence beyond that. The work is designed to create sustainable change, not dependency. I make myself irrelevant as quickly as possible.',
  },
  {
    question: 'Do you work with teams or just individuals?',
    answer: 'Both. I work one-on-one with founders and leaders, and I also facilitate team retreats and group sessions. Many engagements involve a combination — individual coaching for the leader and targeted sessions with their team.',
  },
  {
    question: 'What if I\'m not sure I\'m ready for coaching?',
    answer: 'Most leaders wonder if it\'s the right time. It usually is. The initial conversation is designed to help us both understand where you are and whether coaching is the right intervention. No obligation, no pressure.',
  },
  {
    question: 'How do I know if we\'re the right fit?',
    answer: 'That\'s what the initial conversation is for. Chemistry matters enormously in coaching. If we\'re not the right match, I\'ll be honest about it — and I\'ll point you to someone who might be.',
  },
  {
    question: 'What industries do you specialize in?',
    answer: 'I specialize in creative industries — advertising, design, media, fashion, tech, and the broader creative economy. My 25 years leading creative businesses means I understand your world from the inside.',
  },
  {
    question: 'Can you work with my leadership team on-site?',
    answer: 'Absolutely. I regularly facilitate team retreats and workshops at client offices. These intensive sessions are designed around your team\'s specific dynamics and challenges.',
  },
];

export default function FaqSection() {
  return (
    <section id="faq" data-testid={TEST_IDS.faqSection} className="bg-[#F7F5F2] py-24 md:py-32 px-6 md:px-12 relative">
      <div className="max-w-3xl mx-auto">
        <FadeSlideIn className="mb-12 md:mb-16">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
            <motion.span
              initial={{ width: 0 }}
              whileInView={{ width: 30 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-block h-px bg-[#C9A96E] mr-3 align-middle"
            />
            Questions
          </p>
          <h2
            data-testid={TEST_IDS.faqHeadline}
            className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-[#1B2A4A]"
          >
            <SplitWords>Frequently Asked</SplitWords>
          </h2>
        </FadeSlideIn>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible data-testid={TEST_IDS.faqAccordion} className="w-full">
            {FAQ_ITEMS.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <AccordionItem value={`item-${i}`} className="border-b border-[#1B2A4A]/10 rounded-none">
                  <AccordionTrigger
                    className="font-serif text-lg md:text-xl text-[#1B2A4A] py-6 hover:no-underline hover:text-[#C9A96E] transition-colors duration-300 text-left"
                    data-testid={`faq-trigger-${i}`}
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-[#2D2D2D]/70 font-sans leading-relaxed pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-sm text-[#2D2D2D]/35 font-sans mt-12"
        >
          Still have questions? The initial conversation is free — no pitch, just clarity.
        </motion.p>
      </div>
    </section>
  );
}
